import React, { createContext, useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useSubcontractorProfile } from '@/contexts/SubcontractorProfileContext';
import { useAuth } from '@/contexts/SupabaseAuthContext';

export const UnreadMessagesContext = createContext();

export const useUnreadMessages = () => useContext(UnreadMessagesContext);

export const UnreadMessagesProvider = ({ children }) => {
  const { profile: currentUserProfile, profileLoading } = useSubcontractorProfile();
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastChecked, setLastChecked] = useState(new Date(0)); 
  const isMounted = useRef(true);
  const channelRef = useRef(null);

  const userId = useMemo(() => currentUserProfile?.user_id || user?.id, [currentUserProfile?.user_id, user?.id]);
  const isAdmin = useMemo(() => {
    return currentUserProfile?.role === 'admin' || (!currentUserProfile && user?.user_metadata?.is_admin);
  }, [currentUserProfile?.role, user?.user_metadata?.is_admin]);

  const profileId = currentUserProfile?.id;

  const fetchUnreadCount = useCallback(async () => {
    if (!userId || profileLoading) {
      if (isMounted.current) setUnreadCount(0);
      return;
    }
    
    // Check if we already fetched recently for this specific user to avoid rapid loops
    // especially during 400 error states
    if (Date.now() - lastChecked.getTime() < 1000) {
        return;
    }

    console.log("UnreadMessagesContext: Fetching unread count for user:", userId);

    const isSimulating = currentUserProfile?.isSimulation;

    if (isAdmin && !isSimulating) {
      try {
        const { data: readData, error: readError } = await supabase
          .from('message_read_status')
          .select('message_id')
          .eq('user_id', userId);

        if (readError) throw readError;
        const readIds = (readData || []).map(r => r.message_id);

        let query = supabase
          .from('messages')
          .select('id', { count: 'exact', head: true })
          .not('sender_id', 'is', null)
          .not('sender_id', 'eq', userId);

        if (readIds.length > 0) {
          query = query.not('id', 'in', `(${readIds.join(',')})`);
        }
        
        const { count, error } = await query;
        if (error) throw error;

        if (isMounted.current) {
          setUnreadCount(count || 0);
          setLastChecked(new Date());
        }
      } catch (error) {
        console.error('UnreadMessagesContext: Error fetching admin unread count:', error);
        if (isMounted.current) {
           setUnreadCount(0);
           setLastChecked(new Date());
        }
      }
      return;
    }

    // For Subcontractors (or Admins in Simulation mode)
    try {
      if (!profileId) return;

      // When simulating, we query the message_recipients table directly 
      // instead of the view to bypass RLS restrictions tied to auth.uid()
      const { data: readData, error: readError } = await supabase
        .from('message_read_status')
        .select('message_id')
        .eq('user_id', userId);

      if (readError) throw readError;
      const readIds = (readData || []).map(r => r.message_id);

      // We count messages where this subcontractor is a recipient and has NOT read it
      // This mirrors the view logic but is RLS-transparent for admins
      let query = supabase
        .from('message_recipients')
        .select('id', { count: 'exact', head: true })
        .eq('subcontractor_id', profileId)
        .is('read_at', null);

      if (readIds.length > 0) {
        query = query.not('message_id', 'in', `(${readIds.join(',')})`);
      }
      
      const { count, error } = await query;
      if (error) throw error;

      if (isMounted.current) {
        setUnreadCount(count || 0);
        setLastChecked(new Date());
      }
    } catch (error) {
      console.error('UnreadMessagesContext: Error fetching subcontractor unread count:', error);
      if (isMounted.current) {
        setUnreadCount(0);
        setLastChecked(new Date());
      }
    }
  }, [userId, profileLoading, isAdmin, profileId, currentUserProfile?.isSimulation]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current).catch(err => console.error("UnreadMessagesContext: Error removing channel on unmount:", err));
        channelRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!profileLoading && userId) {
      fetchUnreadCount();
    } else if (!profileLoading && !userId) {
      if (isMounted.current) setUnreadCount(0);
    }
  }, [userId, profileLoading, fetchUnreadCount]);

  const isSimulating = currentUserProfile?.isSimulation;

  useEffect(() => {
    if (profileLoading || !userId || isSimulating) {
      if (channelRef.current) {
        console.log("UnreadMessagesContext: Cleaning up channel due to loading or lack of ID");
        supabase.removeChannel(channelRef.current).catch(() => {});
        channelRef.current = null;
      }
      return;
    }

    const messagesChannelId = `unread-messages-count-${userId}`;
    
    // Check if we are already subscribed to this specific channel to prevent redundant cycles
    if (channelRef.current && channelRef.current.topic === `realtime:public:${messagesChannelId}`.replace('public:','')) { 
        return;
    }
    
    if (channelRef.current) {
        supabase.removeChannel(channelRef.current).catch(() => {});
        channelRef.current = null;
    }

    console.log("UnreadMessagesContext: Creating new subscription for:", messagesChannelId);

    const newChannel = supabase.channel(messagesChannelId);
    const handleUpdate = () => fetchUnreadCount();

    newChannel.on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'messages' }, 
      handleUpdate
    );

    if (currentUserProfile?.id && !isAdmin) {
      newChannel.on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'message_recipients', filter: `subcontractor_id=eq.${currentUserProfile.id}` },
        handleUpdate
      );
    }

    newChannel.on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'message_read_status', filter: `user_id=eq.${userId}` },
      handleUpdate
    ).subscribe((status) => {
      if (!isMounted.current) return;
      if (status === 'SUBSCRIBED') {
        channelRef.current = newChannel;
      }
    });
    
    return () => {
      if (newChannel) {
        supabase.removeChannel(newChannel).catch(() => {});
      }
    };
  }, [profileLoading, userId, currentUserProfile?.id, isAdmin, isSimulating, fetchUnreadCount]);

  const markMessageAsRead = useCallback(async (messageId) => {
    if (!userId || !messageId) return;
    
    const { error } = await supabase
      .from('message_read_status')
      .upsert({ user_id: userId, message_id: messageId, read_at: new Date().toISOString() }, { onConflict: 'user_id, message_id' });

    if (error) {
      console.error('UnreadMessagesContext: Error marking message as read:', error);
    } else {
      if (currentUserProfile?.id) {
        // Also update the audit log table for admin visibility
        await supabase
          .from('message_recipients')
          .update({ read_at: new Date().toISOString(), delivery_status: 'read' })
          .eq('message_id', messageId)
          .eq('subcontractor_id', currentUserProfile.id)
          .is('read_at', null);
      }
      fetchUnreadCount(); 
    }
  }, [userId, currentUserProfile?.id, fetchUnreadCount]);

  const markAllAsRead = useCallback(async () => {
    if (!userId) return;

    try {
      console.log("UnreadMessagesContext: Marking all messages as read via RPC for user:", userId);
      
      const { error } = await supabase.rpc('mark_all_messages_as_read', {
        p_user_id: userId
      });

      if (error) throw error;

      if (isMounted.current) setUnreadCount(0);
      // Small delay to ensure DB propagation before re-fetching
      setTimeout(() => {
        if (isMounted.current) fetchUnreadCount();
      }, 500);
      return true;
    } catch (error) {
      console.error('UnreadMessagesContext: Error marking all as read:', error);
      return false;
    }
  }, [userId, fetchUnreadCount]);
  
  const value = useMemo(() => ({
    unreadCount,
    fetchUnreadCount,
    markMessageAsRead,
    markAllAsRead,
    lastChecked,
  }), [unreadCount, fetchUnreadCount, markMessageAsRead, markAllAsRead, lastChecked]);

  return (
    <UnreadMessagesContext.Provider value={value}>
      {children}
    </UnreadMessagesContext.Provider>
  );
};